import { api } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AlertCircle,
	Bell,
	CheckCircle,
	Clock,
	Loader2,
	RefreshCw,
	Shield,
} from "lucide-react";
import { useEffect, useState } from "react";
import type {
	AlertSettings,
	CategoryResponse,
	IntegrationInfo,
	TelegramSettings,
} from "./settings-types";
import {
	CardSkeleton,
	NumberField,
	SaveButton,
	ToggleRow,
} from "./settings-ui";

export function AlertsNotificationsSection() {
	return (
		<div className="space-y-4">
			<h2 className="font-display text-lg text-stone-800">
				Alerts & Benachrichtigungen
			</h2>
			<AlertThresholdsCard />
			<TelegramCard />
		</div>
	);
}

function AlertThresholdsCard() {
	const queryClient = useQueryClient();
	const query = useQuery({
		queryKey: ["settings", "alert"],
		queryFn: () =>
			api.get<CategoryResponse<AlertSettings>>("/settings/category/alert"),
	});

	const [form, setForm] = useState<AlertSettings>({
		inquiryDays: 3,
		invoiceOverdueDays: 14,
		upcomingEventDays: 7,
		offerNoResponseDays: 5,
	});

	useEffect(() => {
		if (query.data) setForm(query.data.data);
	}, [query.data]);

	const mutation = useMutation({
		mutationFn: (data: AlertSettings) =>
			api.put("/settings/category/alert", data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["settings", "alert"] });
			queryClient.invalidateQueries({ queryKey: ["alerts"] });
		},
	});

	if (query.isLoading) return <CardSkeleton />;

	return (
		<div className="space-y-3">
			<h2 className="font-medium text-stone-700 flex items-center gap-2">
				<Shield size={16} className="text-amber-600" />
				Alert-Schwellwerte
			</h2>
			<div className="card space-y-4">
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<NumberField
						label="Anfrage ohne Angebot (Tage)"
						value={form.inquiryDays}
						onChange={(v) => setForm((f) => ({ ...f, inquiryDays: v }))}
					/>
					<NumberField
						label="Rechnung überfällig (Tage)"
						value={form.invoiceOverdueDays}
						onChange={(v) => setForm((f) => ({ ...f, invoiceOverdueDays: v }))}
					/>
					<NumberField
						label="Event bald (Tage)"
						value={form.upcomingEventDays}
						onChange={(v) => setForm((f) => ({ ...f, upcomingEventDays: v }))}
					/>
					<NumberField
						label="Angebot ohne Rückmeldung (Tage)"
						value={form.offerNoResponseDays}
						onChange={(v) =>
							setForm((f) => ({ ...f, offerNoResponseDays: v }))
						}
					/>
				</div>
				<SaveButton
					isPending={mutation.isPending}
					isSuccess={mutation.isSuccess}
					onClick={() => mutation.mutate(form)}
				/>
			</div>
		</div>
	);
}

function TelegramCard() {
	const queryClient = useQueryClient();

	const statusQuery = useQuery({
		queryKey: ["settings", "status"],
		queryFn: () =>
			api.get<{ data: Record<string, IntegrationInfo> }>("/settings/status"),
	});

	const query = useQuery({
		queryKey: ["settings", "telegram"],
		queryFn: () =>
			api.get<CategoryResponse<TelegramSettings>>(
				"/settings/category/telegram",
			),
	});

	const [form, setForm] = useState<TelegramSettings>({
		notifyNewBooking: true,
		notifyOverdue: true,
		dailyBriefing: true,
		quietStart: 23,
		quietEnd: 7,
	});

	const [testResult, setTestResult] = useState<{
		ok: boolean;
		message: string;
	} | null>(null);

	useEffect(() => {
		if (query.data) setForm(query.data.data);
	}, [query.data]);

	const mutation = useMutation({
		mutationFn: (data: TelegramSettings) =>
			api.put("/settings/category/telegram", data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["settings", "telegram"] });
		},
	});

	const testMutation = useMutation({
		mutationFn: () =>
			api.post<{ data: { ok: boolean; message: string } }>(
				"/settings/test/telegram",
			),
		onSuccess: (res) => setTestResult(res.data),
	});

	const configured = statusQuery.data?.data?.telegram?.configured ?? false;

	if (query.isLoading) return <CardSkeleton />;

	return (
		<div className="space-y-3">
			<h2 className="font-medium text-stone-700 flex items-center gap-2">
				<Bell size={16} className="text-purple-600" />
				Benachrichtigungen (Telegram)
				{statusQuery.data && (
					<span
						className={`ml-auto text-xs px-2 py-0.5 rounded-full ${configured ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
					>
						{configured ? "Verbunden" : "Nicht konfiguriert"}
					</span>
				)}
			</h2>
			<div className="card space-y-4">
				{!configured && (
					<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
						<div className="flex items-start gap-2">
							<AlertCircle size={16} className="mt-0.5 shrink-0" />
							<div>
								<p className="font-medium mb-1">Konfiguration erforderlich</p>
								<p className="text-xs text-amber-700">
									Bot-Token und Chat-ID werden als Umgebungsvariablen
									konfiguriert (Cloudflare Dashboard → Worker Settings →
									Variables):
								</p>
								<ul className="text-xs text-amber-700 mt-1 list-disc ml-4 space-y-0.5">
									<li>
										<code className="bg-amber-100 px-1 rounded">
											TELEGRAM_BOT_TOKEN
										</code>
									</li>
									<li>
										<code className="bg-amber-100 px-1 rounded">
											TELEGRAM_GROUP_CHAT_ID
										</code>
									</li>
								</ul>
							</div>
						</div>
					</div>
				)}

				<ToggleRow
					label="Neue Buchung"
					checked={form.notifyNewBooking}
					onChange={(v) => setForm((f) => ({ ...f, notifyNewBooking: v }))}
					disabled={!configured}
				/>
				<ToggleRow
					label="Überfällige Rechnungen"
					checked={form.notifyOverdue}
					onChange={(v) => setForm((f) => ({ ...f, notifyOverdue: v }))}
					disabled={!configured}
				/>
				<ToggleRow
					label="Tägliches Briefing (7:00 UTC)"
					checked={form.dailyBriefing}
					onChange={(v) => setForm((f) => ({ ...f, dailyBriefing: v }))}
					disabled={!configured}
				/>
				<div className="flex items-center gap-3 text-sm text-stone-600">
					<Clock size={14} className="text-stone-400" />
					<span>Ruhezeit:</span>
					<input
						type="number"
						min={0}
						max={23}
						value={form.quietStart}
						onChange={(e) =>
							setForm((f) => ({ ...f, quietStart: Number(e.target.value) }))
						}
						className="input w-16 text-center text-sm"
						disabled={!configured}
					/>
					<span>bis</span>
					<input
						type="number"
						min={0}
						max={23}
						value={form.quietEnd}
						onChange={(e) =>
							setForm((f) => ({ ...f, quietEnd: Number(e.target.value) }))
						}
						className="input w-16 text-center text-sm"
						disabled={!configured}
					/>
					<span>Uhr</span>
				</div>

				<div className="flex items-center gap-3">
					<SaveButton
						isPending={mutation.isPending}
						isSuccess={mutation.isSuccess}
						onClick={() => mutation.mutate(form)}
					/>
					<button
						type="button"
						disabled={testMutation.isPending}
						onClick={() => {
							setTestResult(null);
							testMutation.mutate();
						}}
						className="btn-secondary text-xs !py-1.5 !px-3"
					>
						{testMutation.isPending ? (
							<Loader2 size={12} className="animate-spin inline mr-1" />
						) : (
							<RefreshCw size={12} className="inline mr-1" />
						)}
						Verbindung testen
					</button>
					{testResult && (
						<span
							className={`text-xs flex items-center gap-1 ${testResult.ok ? "text-green-600" : "text-red-600"}`}
						>
							{testResult.ok ? (
								<CheckCircle size={12} />
							) : (
								<AlertCircle size={12} />
							)}
							{testResult.message}
						</span>
					)}
				</div>
			</div>
		</div>
	);
}
